import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, CircularProgress, DialogContent } from '@mui/material';
import styles from './AssignProgramDialog.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import SearchBar from '../atoms/SearchBar';
import ProgramRow from './ProgramRow';
import { useSessionData } from '../../providers';
import { fetchProgramsPageByTrainerIds } from '../../api';
import { queryKeys } from '../../api/queryKeys';
import { getPagedMeta } from '../../api/pagination';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// import { addDays, subDays } from 'date-fns';

import type { PagedResponse, Program } from '../../types';

const PAGE_SIZE_MAX = 100;

export interface AssignProgramDialogProps {
  onClose: () => void;
  onSubmit: (program: Program) => Promise<void>;
  isOpen: boolean;
}

const AssignProgramDialog: React.FC<AssignProgramDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { programs: cachedPrograms, trainers, loginTrainer } = useSessionData();
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [noProgramSelectedError, setNoProgramSelectedError] = useState<boolean>(false);
  const wasOpenRef = useRef(isOpen);
  const debouncedSearchQuery = useDebouncedValue(searchQuery.trim());

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      setSelectedProgram(null);
      setSearchQuery('');
      setSelectedDate(new Date());
      setNoProgramSelectedError(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const trainerIds = useMemo(
    () =>
      Object.keys(trainers).length
        ? Object.keys(trainers)
        : loginTrainer?.id
          ? [loginTrainer.id]
          : [],
    [loginTrainer?.id, trainers],
  );

  const {
    data: programsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingPrograms,
  } = useInfiniteQuery<PagedResponse<Program>>({
    queryKey: queryKeys.programs.trainerIdsPage(trainerIds, {
      page: 0,
      size: PAGE_SIZE_MAX,
      search: debouncedSearchQuery || undefined,
    }),
    queryFn: async ({ pageParam }) => {
      if (!trainerIds.length) {
        return { content: [] };
      }
      const result = await fetchProgramsPageByTrainerIds(trainerIds, {
        page: Number(pageParam),
        size: PAGE_SIZE_MAX,
        search: debouncedSearchQuery || undefined,
      });
      if (!result.success || !result.data) {
        return { content: [] };
      }
      return result.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const meta = getPagedMeta(lastPage);
      const nextPage = meta.page + 1;
      return nextPage < meta.totalPages ? nextPage : undefined;
    },
    enabled: isOpen && trainerIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const fetchedPrograms = useMemo(
    () => programsPages?.pages.flatMap((page) => page.content ?? []) ?? [],
    [programsPages],
  );

  const handleProgramsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 80) {
      void fetchNextPage();
    }
  };

  const handleSubmit = async () => {
    if (!selectedProgram) {
      setNoProgramSelectedError(true);
      return;
    }
    setLoading(true);
    const assignedProgram = {
      ...selectedProgram,
      startDate: selectedDate.toISOString(),
    };
    await onSubmit(assignedProgram);
    setLoading(false);
  };

  const hasFetchedProgramPages = Boolean(programsPages?.pages.length);
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const availablePrograms: Program[] = hasFetchedProgramPages
    ? fetchedPrograms
    : Object.values(cachedPrograms);
  const filteredPrograms = availablePrograms
    .filter(({ name }) => {
      if (!normalizedQuery) return true;
      return name.toLowerCase().includes(normalizedQuery);
    })
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const isInitialLoad = isLoadingPrograms && availablePrograms.length === 0;

  const renderSelectionView = () => {
    return (
      <DialogContent className={styles.assignProgramDialog}>
        <SearchBar
          className={styles.assignProgramSearch}
          placeholder="Search"
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
        {noProgramSelectedError && (
          <span className={styles.fieldErrorText}>
            Please select a program
          </span>
        )}
        {isInitialLoad ? (
          <div className={styles.loadingPrograms}>
            <CircularProgress size={32} color="primary" />
          </div>
        ) : (
          <div className={styles.programItems} onScroll={handleProgramsScroll}>
            {filteredPrograms.map((program, index) => {
              const isSelected = selectedProgram?.id === program.id;
              return (
                <div
                  onClick={() => {
                    setNoProgramSelectedError(false);
                    setSelectedProgram(program);
                  }}
                  className={classNames(styles.programItemWrapper, {
                    [styles.selectedProgramItem]: isSelected,
                  })}
                  key={`programItem-${program.name}-${index}`}
                >
                  <ProgramRow
                    minimalDetails={true}
                    program={program}
                    selected={isSelected}
                  />
                </div>
              );
            })}
            {isFetchingNextPage && (
              <div className={styles.loadingMorePrograms}>
                <CircularProgress size={24} color="primary" />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    );
  };

  const renderCalendarView = () => {
    return (
      <DialogContent className={styles.calendarDialog}>
        <DatePicker
          selected={selectedDate}
          onChange={(date: Date | null) => {
            if (date) setSelectedDate(date);
          }}
          inline
          // minDate={subDays(new Date(), 0)} // Optional: Disable past dates
          // maxDate={addDays(new Date(), 365)} // Optional: Restrict future dates
          calendarClassName={styles.customCalendar} // Link to custom styling
        />
      </DialogContent>
    );
  };

  const renderContentView = () => {
    return selectedProgram ? renderCalendarView() : renderSelectionView();
  };

  return (
    <BaseDialog
      onClose={onClose}
      isOpen={isOpen}
      title={
        selectedProgram ? 'Select Start Date' : 'Select a Program to Assign'
      }
      actions={
        <Button
          disabled={loading}
          className={styles.submit}
          onClick={handleSubmit}
        >
          {loading ? (
            <CircularProgress size={24} color="primary" />
          ) : (
            <span className={styles.submitText}>Assign Program</span>
          )}
        </Button>
      }
    >
      {renderContentView()}
    </BaseDialog>
  );
};

export default AssignProgramDialog;